import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Building2, MapPin, TrendingUp, TrendingDown,
  Bed, Bath, Square, Clock, Home, BarChart3, Map, FileText, Sparkles,
  Loader2, AlertCircle
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const SharedReportPage = () => {
  const { shareId } = useParams();
  const [report, setReport] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSharedReport();
  }, [shareId]);

  const fetchSharedReport = async () => {
    try {
      const response = await axios.get(`${API}/shared/${shareId}`);
      setReport(response.data);
      setBranding(response.data.branding);
    } catch (err) {
      if (err.response?.status === 410) {
        setError("This share link has expired.");
      } else if (err.response?.status === 404) {
        setError("Report not found.");
      } else {
        setError("Failed to load report.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-12 w-12 animate-spin text-teal-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-navy-900 mb-2">{error}</h2>
            <p className="text-slate-600 mb-6">
              The report you're looking for is no longer available.
            </p>
            <Link to="/">
              <Button className="bg-teal-500 hover:bg-teal-400 text-navy-900">
                Go to Rentlytix
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!report) return null;

  const rentEstimate = report.rent_estimate;
  const comparables = report.comparables || [];
  const marketComparison = report.market_comparison;

  // Prepare chart data
  const rentDistribution = comparables.reduce((acc, comp) => {
    const bucket = Math.floor(comp.rent / 250) * 250;
    const key = `$${bucket}-${bucket + 250}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const chartData = Object.entries(rentDistribution)
    .map(([range, count]) => ({ range, count }))
    .sort((a, b) => parseInt(a.range.replace(/\D/g, "")) - parseInt(b.range.replace(/\D/g, "")));

  const brandColor = branding?.brand_color || "#2DD4BF";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header with Branding */}
      <header className="bg-white border-b border-slate-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {branding?.logo_url ? (
                <img src={branding.logo_url} alt="Logo" className="h-10" />
              ) : (
                <div 
                  className="h-10 w-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: brandColor }}
                >
                  <Building2 className="h-6 w-6 text-white" />
                </div>
              )}
              <div>
                <h1 className="font-heading font-bold text-lg text-navy-900">
                  {branding?.company_name || "Rental Analysis Report"}
                </h1>
                {branding?.contact_email && (
                  <p className="text-sm text-slate-500">{branding.contact_email}</p>
                )}
              </div>
            </div>
            <Link to="/">
              <Button variant="outline" size="sm">
                <Building2 className="h-4 w-4 mr-2" />
                Try Rentlytix
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Property Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-navy-900 mb-2">
                {report.address}
              </h2>
              <p className="text-slate-600 flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                {report.city}, {report.state} {report.zip_code}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="px-3 py-1">
                <Home className="h-3 w-3 mr-1" />
                {report.property_type.replace("_", " ")}
              </Badge>
              <Badge variant="secondary" className="px-3 py-1">
                <Bed className="h-3 w-3 mr-1" />
                {report.bedrooms} BR
              </Badge>
              <Badge variant="secondary" className="px-3 py-1">
                <Bath className="h-3 w-3 mr-1" />
                {report.bathrooms} BA
              </Badge>
              {report.sqft && (
                <Badge variant="secondary" className="px-3 py-1">
                  <Square className="h-3 w-3 mr-1" />
                  {report.sqft.toLocaleString()} sqft
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card style={{ backgroundColor: brandColor }} className="text-white">
            <CardContent className="p-6">
              <p className="text-sm opacity-80 mb-1">Estimated Rent</p>
              <p className="font-data text-3xl font-bold">
                ${rentEstimate.estimated_rent.toLocaleString()}
                <span className="text-lg font-normal opacity-80">/mo</span>
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-slate-500 mb-1">Market Average</p>
              <p className="font-data text-2xl font-bold text-navy-900">
                ${rentEstimate.rent_average.toLocaleString()}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-slate-500 mb-1">Market Range</p>
              <p className="font-data text-lg font-bold text-navy-900">
                ${rentEstimate.rent_low.toLocaleString()} - ${rentEstimate.rent_high.toLocaleString()}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-slate-500 mb-1">Comparables</p>
              <p className="font-data text-2xl font-bold text-navy-900">
                {rentEstimate.comps_count}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Market Comparison */}
        {marketComparison && (
          <Card className="mb-8 border-l-4" style={{ borderLeftColor: marketComparison.status === "above_market" ? "#10B981" : "#EF4444" }}>
            <CardContent className="p-6">
              <div className="flex items-start space-x-4">
                {marketComparison.status === "above_market" ? (
                  <TrendingUp className="h-8 w-8 text-emerald-500 flex-shrink-0" />
                ) : (
                  <TrendingDown className="h-8 w-8 text-red-500 flex-shrink-0" />
                )}
                <div>
                  <p className="font-semibold text-navy-900 mb-1">
                    Current rent is {Math.abs(marketComparison.difference_percent).toFixed(1)}% {marketComparison.status.replace("_", " ")}
                  </p>
                  <p className="text-slate-600">
                    At ${marketComparison.current_rent.toLocaleString()}/mo, the property is 
                    {marketComparison.difference > 0 ? " above " : " below "}
                    the estimated market rent of ${marketComparison.estimated_rent.toLocaleString()}/mo.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs Section */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-navy-900 data-[state=active]:text-white">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="map" className="data-[state=active]:bg-navy-900 data-[state=active]:text-white">
              <Map className="h-4 w-4 mr-2" />
              Map
            </TabsTrigger>
            <TabsTrigger value="comps" className="data-[state=active]:bg-navy-900 data-[state=active]:text-white">
              <FileText className="h-4 w-4 mr-2" />
              Comparables
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Rent Distribution</CardTitle>
                  <CardDescription>Distribution of comparable rents</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="range" fontSize={11} tick={{ fill: '#64748b' }} />
                        <YAxis fontSize={11} tick={{ fill: '#64748b' }} />
                        <Tooltip />
                        <Bar dataKey="count" fill={brandColor} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Market Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b">
                      <span className="text-slate-600">25th Percentile</span>
                      <span className="font-data font-semibold">${rentEstimate.percentile_25.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b">
                      <span className="text-slate-600">Median Rent</span>
                      <span className="font-data font-semibold">${rentEstimate.rent_median.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b">
                      <span className="text-slate-600">75th Percentile</span>
                      <span className="font-data font-semibold">${rentEstimate.percentile_75.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <span className="text-slate-600">Properties Analyzed</span>
                      <span className="font-data font-semibold">{rentEstimate.comps_count}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Map Tab */}
          <TabsContent value="map">
            <Card>
              <CardContent className="p-0">
                <div className="h-[500px] rounded-lg overflow-hidden">
                  <MapContainer
                    center={[report.latitude, report.longitude]}
                    zoom={14}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[report.latitude, report.longitude]}>
                      <Popup>
                        <strong>Subject Property</strong><br />
                        {report.address}<br />
                        Est. Rent: ${rentEstimate.estimated_rent.toLocaleString()}/mo
                      </Popup>
                    </Marker>
                    <Circle
                      center={[report.latitude, report.longitude]}
                      radius={1609}
                      pathOptions={{ color: brandColor, fillColor: brandColor, fillOpacity: 0.1 }}
                    />
                    {comparables.slice(0, 10).map((comp, index) => (
                      <Marker key={index} position={[comp.latitude, comp.longitude]}>
                        <Popup>
                          <strong>{comp.address}</strong><br />
                          Rent: ${comp.rent.toLocaleString()}/mo<br />
                          {comp.bedrooms} BR / {comp.bathrooms} BA
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Comparables Tab */}
          <TabsContent value="comps">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Comparable Properties</CardTitle>
                <CardDescription>{comparables.length} properties analyzed</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Address</TableHead>
                        <TableHead>Rent</TableHead>
                        <TableHead>Beds</TableHead>
                        <TableHead>Baths</TableHead>
                        <TableHead>Distance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {comparables.map((comp, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{comp.address}</TableCell>
                          <TableCell className="font-data">${comp.rent.toLocaleString()}</TableCell>
                          <TableCell>{comp.bedrooms}</TableCell>
                          <TableCell>{comp.bathrooms}</TableCell>
                          <TableCell>{comp.distance_miles.toFixed(2)} mi</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="mt-8 pt-8 border-t border-slate-200 text-center">
          <p className="text-sm text-slate-500 mb-4">
            Report generated: {new Date(report.created_at).toLocaleString()}
          </p>
          <Link to="/">
            <Button className="bg-teal-500 hover:bg-teal-400 text-navy-900">
              <Building2 className="h-4 w-4 mr-2" />
              Get Your Own Rent Analysis
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default SharedReportPage;
